import React, { useEffect, useRef, useState } from "react";
import { LBMCHeader } from "@lbmc-digital/lbmc-ui-kit";
import ICONS from "../Constants/Icons";
import SearchBox from "./SearchBox";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Person } from "@microsoft/mgt-react";
import useAccessToken from "../Custom/useAccessToken";
import { getProfileDetails } from "../Services/GetService";
import SearchSuggestions from "./SearchSuggestions";
import { useDebounce } from 'primereact/hooks';

interface HeaderProps {
  onSearch: (query: string) => void;
}

const Header: React.FC<HeaderProps> = ({ onSearch }) => {
  // const [searchQuery, setSearchQuery] = useState("");
  const icons = ICONS();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAccessToken();
  const [entraId, setEntraId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [shortCode, setShortCode] = useState<string | null>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const criteria = searchParams?.get("criteria") ?? "";
  const [searchQuery, debouncedValue, setSearchQuery] = useDebounce('', 320);   
  const [closeSignal, setCloseSignal] = useState(0);

  useEffect(() => {
    const loadProfile = async () => {
      if (!accessToken) return;
      try {
        const profile: any = await getProfileDetails(accessToken);
        if (profile) {
          setShortCode(profile.profileShortCode || profile.profileId || null);
          setName(profile.displayName || profile.name || null);
          setEntraId(
            profile.entraId || profile.entraID || profile.profileId || null
          );
        }
      } catch (e) {
        console.debug("Failed to load profile for header", e);
      }
    };

    loadProfile();
  }, [accessToken]);

  const handleLogoClick = () => {
    setSearchQuery("");
    if (location.pathname === "/") {
      navigate("/", { state: null, replace: true });
      window.location.reload();
    } else {
      navigate("/", { state: null });
    }
  };

  useEffect(() => {

    if (location.state) {

      const st = location.state as { from?: string; query?: string } | null;
      
      if (st?.from === "search" && st.query) {
        setSearchQuery(st.query);
      } else {
        setSearchQuery("");
      }
    }
    else if (location.pathname === "/search" && location.state == null && criteria) {
      // setSearchQuery(criteria);
      // if (typeof onSearch === "function") onSearch(criteria);
      navigate(`/search?criteria=${criteria}`, { state: { from: "search", query: criteria } });
    }
  }, [location]);

  const handleSearch = (query: string) => {

    setSearchQuery(query);
    const encoded = encodeURIComponent(query.trim()); 

    if ((location.pathname === "/" || location.pathname === "/search" || location.pathname.startsWith("/profile/")) && query) {
      setCloseSignal(prev => prev + 1);    
      navigate(`/search?criteria=${encoded}`, { state: { from: "search", query: query } });
    } 
    else if( location.pathname === "/search" && !query) {       
      setCloseSignal(prev => prev + 1);     
      navigate("/", { replace: true });
    }
    else if (location.pathname === "/" && !query) {
      return;
    } 
    else if (location.pathname.startsWith("/profile/") && !query) {
      return;
    }
    else {       
      return;
    }
  };

  return (
    <>
      <LBMCHeader
        items={[
          {
            element: (
              <img
                src={icons.NexusLogo}
                alt="Nexuslogo"
                className="header-log mt-1 ml-1"
                height="20px"
                style={{ cursor: "pointer" }}
                onClick={handleLogoClick}
              />
            ),
            key: "logo",
          },
          {
            element: (
              <div className="search-box">
                <SearchBox
                  query={searchQuery}
                  setQuery={setSearchQuery}
                  onSearch={handleSearch}
                />
              </div>
            ),
            key: "profile search",
          },
        ]}
        rightContent={
          <div>
            {entraId && (
              <div className="flex align-items-center gap-2">
                <Person
                  userId={entraId}
                  personCardInteraction="hover"
                  showPresence={true}
                  avatarSize="small"
                />
                <button
                  className="p-link"
                  onClick={() => {
                    if (shortCode) navigate(`/profile/${shortCode}`);
                    else navigate("/profile");
                  }}
                >
                  <h4 className="header-username">{name}</h4>
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="">
        <SearchSuggestions searchQuery={debouncedValue} searchBoxRef={searchBoxRef} closeSignal={closeSignal} />
      </div>
    </>
  );
};

export default Header;
